import {copyObject, CoreDI, inject} from '@gongxigames/framework/core';
import {GlobalEventsMapForBackwardCompatibility} from '@gongxigames/framework/slot/events';
import {
    GenericPopupConfig,
    GenericPopupData,
    PopupAnimations,
    PopupSpineConfig,
    PopupTypedAnimation,
    GameConfig
} from '@gongxigames/framework/slot/types';
import {DI} from '@gongxigames/framework/slot/configs/DI';
import {SoundManager} from '@gongxigames/framework/slot/services/SoundManager';
import {GenericPopupEvents} from '@gongxigames/framework/slot/modules/genericPopup/GenericPopupEvents';
import {GenericPopupView} from '@gongxigames/framework/slot/modules/genericPopup/GenericPopupView';
import {GenericPopupBasicState} from '@gongxigames/framework/slot/modules/genericPopup/satates/GenericPopupBasicState';
import {
    GenericPopupShowingState
} from '@gongxigames/framework/slot/modules/genericPopup/satates/GenericPopupShowingState';
import {Module} from '@gongxigames/framework/slot';

export type GenericPopupModuleEvents = GlobalEventsMapForBackwardCompatibility & {
    'GenericPopupModuleEvents.FADE_IN_FINISHED': symbol;
    'GenericPopupModuleEvents.FADE_OUT_STARTED': symbol;
    'GenericPopupModuleEvents.CONTINUE_BUTTON_CLICKED': symbol;
    'GenericPopupModuleEvents.CLOSED': symbol;
};

export type QueueData = {
    type: Symbol;
    args: any[] | GenericPopupData;
};

export class GenericPopupModule extends Module<GenericPopupView, GenericPopupModuleEvents> {
    public static NAME = Symbol('GenericPopupModule');
    public static readonly BASIC_STATE: string = 'basic';
    public static readonly SHOWING_STATE: string = 'showing';

    @inject(DI.SoundManager)
    protected _soundManager!: SoundManager;

    @inject(CoreDI.GameConfig)
    protected _gameConfig!: GameConfig;

    protected _queue: QueueData[] = [];
    protected _namesOfViewsWithEventsListeners: symbol[] = [];
    protected _showPopupResolve?: () => void;

    protected setupEvents(): void {
        super.setupEvents();

        this.onCurrentStateHandle(GenericPopupEvents.SHOW_POPUP);
        this.onCurrentStateHandle('General.Popup.SHOW');
        this.onCurrentStateHandle('General.UI.SPACEBAR_UP');

    }

    protected setUpEventsOnView(): void {
        this.onViewCurrentStateHandle('GenericPopupModuleEvents.CONTINUE_BUTTON_CLICKED');
        this.onViewCurrentStateHandle('GenericPopupModuleEvents.CLOSED');
        this.onViewCurrentStateHandle('GenericPopupModuleEvents.FADE_OUT_STARTED');
        this.onViewCurrentStateHandle('GenericPopupModuleEvents.FADE_IN_FINISHED');
    }

    protected setupViews(): void {
        this.setView(GenericPopupView.NAME);
    }

    protected setupStates(): void {
        this.addState(new GenericPopupBasicState(this, GenericPopupModule.BASIC_STATE));
        this.addState(new GenericPopupShowingState(this, GenericPopupModule.SHOWING_STATE));

        this.gotoState(GenericPopupModule.BASIC_STATE);
    }

    public getConfigByType(type: Symbol, data?: string, ...args: any[]): GenericPopupConfig | null | undefined {
        const popupConfig = this.gameConfig.popupConfig?.find(value => value.type === type);

        if (popupConfig) {
            if (data) {
                this.setValueConfig(popupConfig.spine, data);
            }

            if (args && args.length > 0) {
                this.checkConfigArguments(popupConfig, args);
            }
        }

        return popupConfig;
    }

    public createConfigWithTypedAnimations(config: GenericPopupConfig, type: number): GenericPopupConfig {
        const newConfig = copyObject(config);

        newConfig.spine.forEach((spineConfig: PopupSpineConfig) => {
            const {animation} = spineConfig;

            if (animation) {
                spineConfig.animation = this.getTypedAnimations(animation, type);
            }
        });

        return {
            ...newConfig,
            view: config.view,
            type: config.type
        };
    }

    public playTypedAnimationSounds(config: GenericPopupConfig, type: number): void {
        config.spine.forEach(spineConfig => {
            const {animation} = spineConfig;
            const {start, idle, skin} = animation as PopupAnimations;
            const playSound = (animations: PopupTypedAnimation[]) => {
                const sound = animations?.find(value => value.type === type)!.sound;

                if (sound) {
                    this.soundPlayer.play(sound);
                }
            };

            start && typeof start !== 'string' && playSound(start);
            idle && typeof idle !== 'string' && playSound(idle);
            skin && typeof skin !== 'string' && playSound(skin);
        });
    }

    /**
     * For override
     */
    protected checkConfigArguments(_popupConfig: GenericPopupConfig, _args: any[]): void {
    }

    public setupView(name: symbol): void {
        this.setView(name);

        if (this._namesOfViewsWithEventsListeners.indexOf(name) === -1) {
            this.setUpEventsOnView();

            this._namesOfViewsWithEventsListeners.push(name);
        }
    }

    protected setValueConfig(spineConfigs: PopupSpineConfig[], value: string): void {
        spineConfigs.forEach(spineConfig => {
            if (spineConfig.value) {
                const {text, richText} = spineConfig.value;

                if (richText) {
                    richText.values = {value};
                } else if (text) {
                    text.text = value;
                }
            }
        });
    }

    protected getTypedAnimations(animations: PopupAnimations, type: number): PopupAnimations {
        const {start, idle, skin} = animations;
        const getName = (animations: PopupTypedAnimation[]) => animations?.find(value => value.type === type)!.name;

        return {
            start: start && getName(start as PopupTypedAnimation[]),
            idle: idle && getName(idle as PopupTypedAnimation[]),
            skin
        };
    }

    public get soundManager(): SoundManager {
        return this._soundManager;
    }

    public get gameConfig(): GameConfig {
        return this._gameConfig;
    }

    public get queue(): QueueData[] {
        return this._queue;
    }
}
